/**
 * TripsManagementPage — CRUD admin pour Carnets de voyage (Blocs 3 + 2 + 4 + 5).
 * Route : /dashboard/trips
 *
 * Features :
 * - Liste avec colonnes : Titre / Type / Dates / Statut / Nb jours
 * - Actions : Modifier / Prévisualiser (nouvel onglet, draft OK admin) / Publier-Dépublier / Supprimer
 * - Redirection post-create vers l'editeur (comme demande Bloc 2)
 * - Editeur : experience_type, show_polyline, dates, chapo, sous-titre
 * - Editeur journee : title, at_sea toggle, autocomplete geocodage
 * - Warning cohérence dates (Bloc 5)
 */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  getTripsAdmin,
  createTrip,
  updateTrip,
  deleteTrip,
  createTripDay,
  updateTripDay,
  deleteTripDay,
  reorderTripDays,
  getTripBySlug,
  geocode,
  aiGenerateTripIntro,
  exportTripDayPackage,
  importTripDayEditorial,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import ContentBlocksEditor from "../components/ContentBlocksEditor";
import TripDayAIAssistant from "../components/TripDayAIAssistant";
import TripDayMediaSection from "../components/TripDayMediaSection";
import {
  Plus, Trash2, Pencil, ArrowUp, ArrowDown, Ship, Save, ArrowLeft, MapPin,
  Eye, CheckCircle, XCircle, AlertTriangle, Waves, Calendar as CalIcon, Sparkles, Loader2,
  Compass, BookMarked,
} from "lucide-react";
import { openReporterFromTrip, createPracticalSheet } from "../lib/api";

const STATUS_LABELS = {
  draft: "Brouillon",
  in_review: "En relecture",
  published: "Publié",
  archived: "Archivé",
};

const EXPERIENCE_LABELS = {
  croisiere: "Croisière",
  sejour_club: "Séjours & Clubs",
  circuit: "Circuit",
  city_trip: "City-trip",
  road_trip: "Road-trip",
  autre: "Autre",
};

const EXPERIENCE_COLOR = {
  croisiere: "bg-blue-100 text-blue-700",
  sejour_club: "bg-emerald-100 text-emerald-700",
  circuit: "bg-amber-100 text-amber-700",
  city_trip: "bg-purple-100 text-purple-700",
  road_trip: "bg-orange-100 text-orange-700",
  autre: "bg-gray-100 text-gray-700",
};

export default function TripsManagementPage() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", subtitle: "", chapo: "", status: "draft", experience_type: "croisiere" });

  const loadTrips = async () => {
    setLoading(true);
    try {
      const res = await getTripsAdmin();
      setTrips(res.data);
    } catch {
      toast.error("Impossible de charger les carnets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTrips(); }, []);

  const handleCreate = async () => {
    if (!createForm.title.trim()) { toast.error("Le titre est requis"); return; }
    try {
      const res = await createTrip(createForm);
      toast.success("Carnet créé — vous pouvez ajouter des journées");
      setShowCreate(false);
      setCreateForm({ title: "", subtitle: "", chapo: "", status: "draft", experience_type: "croisiere" });
      await loadTrips();
      setEditing(res.data);  // Bloc 2 : redirect post-create vers l'editeur
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création");
    }
  };

  const handleTogglePublish = async (trip) => {
    const newStatus = trip.status === "published" ? "draft" : "published";
    try {
      await updateTrip(trip.id, { status: newStatus });
      toast.success(newStatus === "published" ? "Carnet publié" : "Carnet dépublié");
      loadTrips();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (trip) => {
    if (!window.confirm(`Supprimer définitivement le carnet "${trip.title}" ?`)) return;
    try {
      await deleteTrip(trip.id);
      toast.success("Carnet supprimé");
      loadTrips();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  const handlePreview = (trip) => {
    window.open(`/carnets-de-voyage/${trip.slug}`, "_blank");
  };

  if (editing) {
    return <TripEditor trip={editing} onBack={() => { setEditing(null); loadTrips(); }} onPreview={handlePreview} />;
  }

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="p-6" data-testid="trips-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-primary flex items-center gap-2">
            <Ship className="h-6 w-6" /> Carnets de voyage
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Gestion des carnets et de leurs journées.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="btn-create-trip">
          <Plus className="h-4 w-4" /> Nouveau carnet
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : trips.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-dashed border-border">
          <Ship className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">Aucun carnet pour l'instant.</p>
          <Button onClick={() => setShowCreate(true)} className="mt-4">Créer le premier</Button>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-primary text-left">
              <tr>
                <th className="p-3 font-semibold">Titre</th>
                <th className="p-3 font-semibold">Type</th>
                <th className="p-3 font-semibold">Dates</th>
                <th className="p-3 font-semibold">Statut</th>
                <th className="p-3 font-semibold text-center">Jours</th>
                <th className="p-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t) => {
                const dayCount = t.day_count ?? (t.days?.length || 0);
                return (
                  <tr key={t.id} className="border-t border-border hover:bg-secondary/30" data-testid={`trip-row-${t.slug}`}>
                    <td className="p-3">
                      <button onClick={() => setEditing(t)} className="text-primary hover:text-accent font-semibold text-left">
                        {t.title}
                      </button>
                      <div className="text-xs text-muted-foreground">/{t.slug}</div>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${EXPERIENCE_COLOR[t.experience_type] || EXPERIENCE_COLOR.autre}`}>
                        {EXPERIENCE_LABELS[t.experience_type] || t.experience_type || "—"}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">{fmtDate(t.start_date)} → {fmtDate(t.end_date)}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${t.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                    </td>
                    <td className="p-3 text-center">{dayCount}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => handlePreview(t)} title="Prévisualiser" data-testid={`btn-preview-${t.slug}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleTogglePublish(t)} title={t.status === "published" ? "Dépublier" : "Publier"} data-testid={`btn-toggle-publish-${t.slug}`}>
                        {t.status === "published" ? <XCircle className="h-4 w-4 text-orange-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setEditing(t)} title="Modifier" data-testid={`btn-edit-${t.slug}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(t)} title="Supprimer" data-testid={`btn-delete-${t.slug}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="dialog-create-trip">
          <DialogHeader><DialogTitle>Nouveau carnet de voyage</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titre *</label>
              <Input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} placeholder="Ex : Croisière Méditerranée 2026" data-testid="input-trip-title" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type d'expérience</label>
              <Select value={createForm.experience_type} onValueChange={(v) => setCreateForm({ ...createForm, experience_type: v })}>
                <SelectTrigger data-testid="select-experience-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EXPERIENCE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sous-titre</label>
              <Input value={createForm.subtitle} onChange={(e) => setCreateForm({ ...createForm, subtitle: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Chapô</label>
              <Textarea value={createForm.chapo} onChange={(e) => setCreateForm({ ...createForm, chapo: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} data-testid="btn-confirm-create-trip">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================== TRIP EDITOR ==================

function TripEditor({ trip: initialTrip, onBack, onPreview }) {
  const [trip, setTrip] = useState(initialTrip);
  const [days, setDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [dayForm, setDayForm] = useState(null);
  const [generatingIntro, setGeneratingIntro] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importJsonText, setImportJsonText] = useState("");
  const [importPreview, setImportPreview] = useState(null);

  const handleExportDay = async () => {
    if (!dayForm?.id) return;
    try {
      const res = await exportTripDayPackage(trip.id, dayForm.id);
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tripday-${dayForm.day_number}-${(dayForm.stop_name || "day").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Package ZIP téléchargé — envoyez-le à ChatGPT.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur export");
    }
  };

  const handleImportPreview = async () => {
    if (!dayForm?.id) return;
    try {
      const parsed = JSON.parse(importJsonText);
      const res = await importTripDayEditorial(trip.id, dayForm.id, parsed, true);
      setImportPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "JSON invalide");
    }
  };

  const handleImportApply = async (mode) => {
    if (!dayForm?.id) return;
    try {
      const parsed = JSON.parse(importJsonText);
      parsed.mode = mode;
      const res = await importTripDayEditorial(trip.id, dayForm.id, parsed, false);
      toast.success(`Réimport OK : ${res.data.blocks_count} blocs (${res.data.mode})`);
      // Recharger la journée
      const updated = importPreview && (mode === "replace" ? importPreview.would_replace : importPreview.would_append);
      if (updated) setDayForm({ ...dayForm, content_blocks: updated });
      setShowImportDialog(false);
      setImportJsonText("");
      setImportPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur import");
    }
  };

  const handleGenerateTripIntro = async () => {
    if ((trip.content_blocks || []).length > 0 || (trip.chapo || "").trim()) {
      if (!window.confirm("Cette introduction va écraser le chapô et les blocs intro actuels du carnet. Continuer ?")) return;
    }
    setGeneratingIntro(true);
    try {
      const res = await aiGenerateTripIntro(trip.id, {});
      setTrip({ ...trip, chapo: res.data.chapo, content_blocks: res.data.content_blocks });
      toast.success("Introduction générée par l'IA — n'oubliez pas d'enregistrer.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur IA";
      const code = e?.response?.status;
      if (code === 502) toast.error(`Fournisseur IA indisponible : ${detail}`);
      else toast.error(detail);
    } finally {
      setGeneratingIntro(false);
    }
  };

  const refreshTrip = async () => {
    try {
      const res = await getTripBySlug(trip.slug);
      setTrip(res.data);
      setDays(res.data.days || []);
    } catch {
      toast.error("Impossible de recharger le carnet");
    }
  };

  useEffect(() => {
    refreshTrip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveTripMeta = async () => {
    setSaving(true);
    try {
      await updateTrip(trip.id, {
        title: trip.title,
        subtitle: trip.subtitle,
        chapo: trip.chapo,
        status: trip.status,
        experience_type: trip.experience_type,
        show_polyline: trip.show_polyline,
        start_date: trip.start_date || null,
        end_date: trip.end_date || null,
      });
      toast.success("Carnet sauvegardé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const openDayCreate = () => {
    setDayForm({
      id: null,
      day_number: (days.length || 0) + 1,
      title: "",
      stop_name: "",
      date: "",
      at_sea: false,
      coordinates: { lat: "", lng: "" },
      content_blocks: [],
      writer_notes: "",
    });
    setShowDayModal(true);
  };

  const openDayEdit = (day) => {
    setDayForm({
      id: day.id,
      day_number: day.day_number,
      title: day.title || "",
      stop_name: day.stop_name,
      date: day.date ? day.date.substring(0, 10) : "",
      at_sea: !!day.at_sea,
      coordinates: day.coordinates ? { lat: String(day.coordinates.lat), lng: String(day.coordinates.lng) } : { lat: "", lng: "" },
      content_blocks: day.content_blocks || [],
      writer_notes: day.writer_notes || "",
    });
    setShowDayModal(true);
  };

  const saveDay = async () => {
    if (!dayForm.stop_name.trim()) { toast.error("Nom d'étape requis"); return; }
    const coords = (dayForm.coordinates.lat && dayForm.coordinates.lng && !dayForm.at_sea)
      ? { lat: parseFloat(dayForm.coordinates.lat), lng: parseFloat(dayForm.coordinates.lng) }
      : null;
    const payload = {
      day_number: parseInt(dayForm.day_number, 10),
      stop_name: dayForm.stop_name,
      title: dayForm.title || null,
      coordinates: coords,
      date: dayForm.date || null,
      at_sea: !!dayForm.at_sea,
      content_blocks: dayForm.content_blocks || [],
      writer_notes: dayForm.writer_notes || null,
    };
    try {
      if (dayForm.id) {
        await updateTripDay(trip.id, dayForm.id, payload);
        toast.success("Journée mise à jour");
      } else {
        await createTripDay(trip.id, payload);
        toast.success("Journée ajoutée");
      }
      setShowDayModal(false);
      refreshTrip();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const removeDay = async (day) => {
    if (!window.confirm(`Supprimer la journée "${day.stop_name}" ?`)) return;
    try { await deleteTripDay(trip.id, day.id); toast.success("Journée supprimée"); refreshTrip(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const moveDay = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= days.length) return;
    const newOrder = [...days];
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    try { await reorderTripDays(trip.id, newOrder.map((d) => d.id)); refreshTrip(); }
    catch { toast.error("Erreur réordonnancement"); }
  };

  // Warning cohérence dates (Bloc 5)
  const dateWarnings = useMemo(() => {
    const warnings = {};
    const sorted = [...days].sort((a, b) => a.day_number - b.day_number);
    for (let i = 0; i < sorted.length; i++) {
      const d = sorted[i];
      if (!d.date) continue;
      const prev = sorted[i - 1];
      const next = sorted[i + 1];
      if (prev?.date && d.date < prev.date) warnings[d.id] = "Date antérieure au jour précédent";
      else if (next?.date && d.date > next.date) warnings[d.id] = "Date postérieure au jour suivant";
    }
    return warnings;
  }, [days]);

  // État éditorial par journée (Bloc IA Carnets)
  const dayEditorialState = (d) => {
    const blocks = d.content_blocks || [];
    if (blocks.length === 0) return { label: "Vide", color: "bg-gray-100 text-gray-700" };
    const words = blocks
      .filter(b => b.type === "paragraph" || b.type === "quote")
      .reduce((acc, b) => acc + (b.text || "").split(/\s+/).filter(Boolean).length, 0);
    if (words < 200) return { label: "Brouillon", color: "bg-amber-100 text-amber-700" };
    return { label: "Rédigé", color: "bg-emerald-100 text-emerald-700" };
  };

  const editorialStats = useMemo(() => {
    if (days.length === 0) return null;
    let rédigées = 0, àCompléter = 0;
    for (const d of days) {
      const s = dayEditorialState(d).label;
      if (s === "Rédigé") rédigées += 1;
      else àCompléter += 1;
    }
    return (
      <span className="text-sm font-normal text-muted-foreground ml-2" data-testid="days-editorial-stats">
        — {rédigées} rédigée{rédigées > 1 ? "s" : ""} · {àCompléter} à compléter
      </span>
    );
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6" data-testid="trip-editor">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="btn-back-trips">
          <ArrowLeft className="h-4 w-4" /> Retour aux carnets
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleGenerateTripIntro} disabled={generatingIntro} className="gap-2" data-testid="btn-ai-trip-intro">
            {generatingIntro ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Intro IA
          </Button>
          <Button variant="outline" onClick={() => onPreview(trip)} className="gap-2" data-testid="btn-preview-current">
            <Eye className="h-4 w-4" /> Prévisualiser
          </Button>
          <Button
            variant="secondary"
            className="gap-2 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30"
            data-testid="btn-open-reporter"
            onClick={async () => {
              if (!trip.id) {
                toast.error("Enregistrez d'abord ce voyage.");
                return;
              }
              try {
                const r = await openReporterFromTrip(trip.id);
                const sid = r.data?.session?.id;
                if (sid) window.location.href = `/reporter?session=${sid}`;
                else toast.error("Impossible d'ouvrir Reporter.");
              } catch (e) {
                toast.error("Erreur : " + (e?.response?.data?.detail || e?.message));
              }
            }}
          >
            <Compass className="h-4 w-4" /> Je suis en voyage — ouvrir Reporter
          </Button>
          <Button onClick={saveTripMeta} disabled={saving} className="gap-2" data-testid="btn-save-trip">
            <Save className="h-4 w-4" /> {saving ? "..." : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg p-6 mb-6">
        <h2 className="font-heading text-xl font-semibold text-primary mb-4">Informations générales</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Titre</label>
            <Input value={trip.title || ""} onChange={(e) => setTrip({ ...trip, title: e.target.value })} data-testid="input-edit-trip-title" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type d'expérience</label>
            <Select value={trip.experience_type || "croisiere"} onValueChange={(v) => setTrip({ ...trip, experience_type: v })}>
              <SelectTrigger data-testid="select-edit-experience-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(EXPERIENCE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Statut</label>
            <Select value={trip.status || "draft"} onValueChange={(v) => setTrip({ ...trip, status: v })}>
              <SelectTrigger data-testid="select-trip-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3 pb-1">
            <div className="flex items-center gap-2">
              <Switch checked={trip.show_polyline !== false} onCheckedChange={(v) => setTrip({ ...trip, show_polyline: v })} data-testid="switch-show-polyline" />
              <label className="text-sm font-medium">Afficher le tracé sur la carte</label>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Sous-titre</label>
            <Input value={trip.subtitle || ""} onChange={(e) => setTrip({ ...trip, subtitle: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Chapô</label>
            <Textarea rows={3} value={trip.chapo || ""} onChange={(e) => setTrip({ ...trip, chapo: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date de début</label>
            <Input type="date" value={(trip.start_date || "").substring(0, 10)} onChange={(e) => setTrip({ ...trip, start_date: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date de fin</label>
            <Input type="date" value={(trip.end_date || "").substring(0, 10)} onChange={(e) => setTrip({ ...trip, end_date: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold text-primary">Journées ({days.length}){editorialStats}</h2>
          <Button onClick={openDayCreate} className="gap-2" data-testid="btn-add-day">
            <Plus className="h-4 w-4" /> Ajouter une journée
          </Button>
        </div>
        {days.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune journée. Ajoutez la première étape.</p>
        ) : (
          <div className="space-y-2">
            {days.map((d, idx) => (
              <div key={d.id} className="flex items-center gap-3 p-3 border border-border rounded" data-testid={`day-row-${d.day_number}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0 text-white ${d.at_sea ? "bg-blue-400" : "bg-primary"}`}>
                  {d.at_sea ? <Waves className="h-5 w-5" /> : d.day_number}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-primary flex items-center gap-2">
                    {d.stop_name}
                    {d.title && <span className="text-xs font-normal text-muted-foreground">· {d.title}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    {d.date && <span className="flex items-center gap-1"><CalIcon className="h-3 w-3" />{new Date(d.date).toLocaleDateString("fr-FR")}</span>}
                    {d.coordinates && !d.at_sea && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{d.coordinates.lat.toFixed(2)}, {d.coordinates.lng.toFixed(2)}</span>}
                    {d.at_sea && <span className="text-blue-600">Journée en mer</span>}
                    {(() => {
                      const s = dayEditorialState(d);
                      return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.color}`} data-testid={`day-editorial-state-${d.day_number}`}>{s.label}</span>;
                    })()}
                    <span className="text-[10px]">· {(d.media_ids || []).length} média{((d.media_ids || []).length > 1 ? "s" : "")}</span>
                  </div>
                  {dateWarnings[d.id] && (
                    <div className="mt-1 text-xs text-amber-700 flex items-center gap-1" data-testid={`date-warning-${d.day_number}`}>
                      <AlertTriangle className="h-3 w-3" /> {dateWarnings[d.id]}
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => moveDay(idx, -1)} disabled={idx === 0}><ArrowUp className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => moveDay(idx, 1)} disabled={idx === days.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => openDayEdit(d)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => removeDay(d)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent className="max-w-3xl" data-testid="dialog-day-editor">
          <DialogHeader>
            <DialogTitle>{dayForm?.id ? "Modifier la journée" : "Nouvelle journée"}</DialogTitle>
          </DialogHeader>
          {dayForm && (
            <div className="space-y-3 py-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Numéro *</label>
                  <Input type="number" min="1" value={dayForm.day_number} onChange={(e) => setDayForm({ ...dayForm, day_number: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <Input type="date" value={dayForm.date} onChange={(e) => setDayForm({ ...dayForm, date: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titre éditorial (optionnel)</label>
                <Input value={dayForm.title} onChange={(e) => setDayForm({ ...dayForm, title: e.target.value })} placeholder="Ex : Naples, entre volcan et pastiches" data-testid="input-day-title" />
              </div>

              <div className="flex items-center gap-2 py-2">
                <Switch checked={dayForm.at_sea} onCheckedChange={(v) => setDayForm({ ...dayForm, at_sea: v })} data-testid="switch-at-sea" />
                <label className="text-sm font-medium">Journée en mer</label>
                <span className="text-xs text-muted-foreground">(pas de lieu ni géocodage)</span>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{dayForm.at_sea ? "Description (libre)" : "Lieu / Escale *"}</label>
                {dayForm.at_sea ? (
                  <Input value={dayForm.stop_name} onChange={(e) => setDayForm({ ...dayForm, stop_name: e.target.value })} placeholder="Ex : Journée en mer — Traversée vers Naples" data-testid="input-day-stop" />
                ) : (
                  <LocationAutocomplete
                    value={dayForm.stop_name}
                    onSelect={(res) => setDayForm({ ...dayForm, stop_name: res.label.split(",")[0].trim(), coordinates: { lat: String(res.lat), lng: String(res.lng) } })}
                    onChange={(v) => setDayForm({ ...dayForm, stop_name: v })}
                  />
                )}
              </div>

              {!dayForm.at_sea && (
                <details className="border border-border rounded p-3">
                  <summary className="text-sm font-medium cursor-pointer">Options avancées (coordonnées manuelles)</summary>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Latitude</label>
                      <Input type="number" step="0.0001" value={dayForm.coordinates.lat} onChange={(e) => setDayForm({ ...dayForm, coordinates: { ...dayForm.coordinates, lat: e.target.value } })} placeholder="43.2965" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Longitude</label>
                      <Input type="number" step="0.0001" value={dayForm.coordinates.lng} onChange={(e) => setDayForm({ ...dayForm, coordinates: { ...dayForm.coordinates, lng: e.target.value } })} placeholder="5.3698" />
                    </div>
                  </div>
                </details>
              )}

              <div className="pt-4 border-t border-border">
                <label className="block text-sm font-semibold text-primary mb-1">Notes pour la rédaction (non publiées)</label>
                <p className="text-xs text-muted-foreground mb-2">
                  Indiquez uniquement les faits, impressions et informations réellement vécus.
                  L'IA utilisera ces notes comme source de vérité.
                </p>
                <Textarea
                  rows={3}
                  value={dayForm.writer_notes || ""}
                  onChange={(e) => setDayForm({ ...dayForm, writer_notes: e.target.value })}
                  placeholder="Ex : Escale de 8h. Visite guidée le matin. Déjeuner au restaurant partenaire. Ambiance ensoleillée."
                  data-testid="input-day-writer-notes"
                />
              </div>

              {dayForm.id && (
                <div className="pt-4 border-t border-border">
                  <TripDayMediaSection tripId={trip.id} dayId={dayForm.id} />
                </div>
              )}

              {dayForm.id && (
                <div className="pt-4 border-t border-border flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    data-testid="btn-day-open-reporter"
                    onClick={async () => {
                      try {
                        const r = await openReporterFromTrip(trip.id, dayForm.id);
                        const sid = r.data?.session?.id;
                        if (sid) window.location.href = `/reporter?session=${sid}`;
                      } catch (e) {
                        toast.error("Erreur : " + (e?.response?.data?.detail || e?.message));
                      }
                    }}
                  >
                    <Compass className="h-4 w-4" /> Ouvrir Reporter sur cette étape
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    data-testid="btn-day-create-practical-sheet"
                    onClick={async () => {
                      try {
                        const payload = {
                          title: dayForm.title || dayForm.stop_name || "",
                          location_name: dayForm.at_sea ? null : (dayForm.stop_name || null),
                          trip_id: trip.id,
                          trip_day_id: dayForm.id,
                          status: "draft",
                        };
                        // Coordonnées éditoriales — jamais GPS brut. On ne
                        // reprend que ce qui est explicitement saisi humainement.
                        const lat = parseFloat(dayForm?.coordinates?.lat);
                        const lng = parseFloat(dayForm?.coordinates?.lng);
                        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                          payload.latitude = lat;
                          payload.longitude = lng;
                        }
                        const r = await createPracticalSheet(payload);
                        if (r.data?.id) {
                          window.location.href = `/dashboard/fiches-pratiques/${r.data.id}`;
                        }
                      } catch (e) {
                        toast.error("Erreur : " + (e?.response?.data?.detail || e?.message));
                      }
                    }}
                  >
                    <BookMarked className="h-4 w-4" /> Créer une fiche pratique
                  </Button>
                </div>
              )}

              {dayForm.id && (
                <div className="pt-4 border-t border-border">
                  <TripDayAIAssistant
                    dayForm={dayForm}
                    setDayForm={setDayForm}
                    tripId={trip.id}
                    dayId={dayForm.id}
                    onExport={handleExportDay}
                    onImport={() => setShowImportDialog(true)}
                  />
                </div>
              )}

              <div className="pt-4 border-t border-border">
                <details className="group" data-testid="tripday-advanced-editor">
                  <summary className="cursor-pointer select-none flex items-center justify-between p-2 -mx-2 hover:bg-secondary/40 rounded">
                    <span className="text-sm font-semibold text-primary flex items-center gap-2">
                      <Pencil className="h-4 w-4" />
                      Édition avancée du contenu ({(dayForm.content_blocks || []).length} bloc{(dayForm.content_blocks || []).length > 1 ? "s" : ""})
                    </span>
                    <span className="text-xs text-muted-foreground group-open:hidden">Ouvrir pour corriger manuellement</span>
                    <span className="text-xs text-muted-foreground hidden group-open:block">Fermer</span>
                  </summary>
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-2">
                      À utiliser <strong>après</strong> l'import de la rédaction, pour corrections manuelles (paragraphes, images, galeries, citations, IA par bloc).
                    </p>
                    <ContentBlocksEditor
                      blocks={dayForm.content_blocks || []}
                      onChange={(blocks) => setDayForm({ ...dayForm, content_blocks: blocks })}
                      targetType={dayForm.id ? "trip_day" : null}
                      targetId={dayForm.id || null}
                    />
                  </div>
                </details>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDayModal(false)}>Annuler</Button>
            <Button onClick={saveDay} data-testid="btn-save-day">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import editorial JSON (Bloc 4) */}
      <Dialog open={showImportDialog} onOpenChange={(o) => { setShowImportDialog(o); if (!o) { setImportPreview(null); setImportJsonText(""); } }}>
        <DialogContent className="max-w-3xl" data-testid="import-editorial-dialog">
          <DialogHeader>
            <DialogTitle>Réimporter du contenu éditorial (JSON)</DialogTitle>
          </DialogHeader>
          {!importPreview ? (
            <div className="space-y-2 py-2">
              <p className="text-xs text-muted-foreground">
                Chargez le JSON de retour éditorial (format <code>itercatus-tripday-v1</code>).
                Aucune image ne sera créée — les <code>media_id</code> référencés doivent déjà exister.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => setImportJsonText(String(ev.target?.result || ""));
                    reader.readAsText(f);
                  }}
                  className="text-sm"
                  data-testid="import-file-input"
                />
                <span className="text-xs text-muted-foreground">ou collez ci-dessous :</span>
              </div>
              <Textarea
                rows={10}
                value={importJsonText}
                onChange={(e) => setImportJsonText(e.target.value)}
                placeholder='{"format_version":"itercatus-tripday-v1","trip_day_id":"...","content_blocks":[...]}'
                style={{ fontFamily: "monospace", fontSize: "12px" }}
                data-testid="import-json-input"
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>Annuler</Button>
                <Button onClick={handleImportPreview} disabled={!importJsonText.trim()} data-testid="import-preview-btn">Prévisualiser</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                Existants : <strong>{importPreview.existing_blocks_count}</strong> blocs · À importer : <strong>{importPreview.incoming_blocks_count}</strong>
              </div>
              <div className="max-h-60 overflow-y-auto border border-border rounded p-2 bg-secondary/20 text-xs" data-testid="import-preview-blocks">
                {(importPreview.would_replace || []).map((b, i) => (
                  <div key={i} className="mb-1"><span className="font-semibold">[{b.type}]</span> {b.text ? b.text.substring(0, 200) : "(media)"}</div>
                ))}
              </div>
              {importPreview.existing_blocks_count > 0 && (
                <p className="text-xs text-amber-700">La journée contient déjà du contenu. Choisissez :</p>
              )}
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => setImportPreview(null)}>Retour</Button>
                {importPreview.existing_blocks_count > 0 && (
                  <Button variant="outline" onClick={() => handleImportApply("append")} data-testid="import-apply-append">Ajouter à la suite</Button>
                )}
                <Button onClick={() => handleImportApply("replace")} data-testid="import-apply-replace">Remplacer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================== LocationAutocomplete (Bloc 4) ==================

function LocationAutocomplete({ value, onChange, onSelect }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await geocode(query);
        setResults(res.data);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); }}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Ex : Marseille"
        data-testid="input-day-stop"
      />
      {loading && <div className="absolute right-3 top-2 text-xs text-muted-foreground">…</div>}
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-border rounded shadow-lg max-h-64 overflow-y-auto" data-testid="geocode-results">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { onSelect(r); setQuery(r.label.split(",")[0].trim()); setOpen(false); }}
              className="w-full text-left p-2 hover:bg-secondary border-b border-border last:border-0"
              data-testid={`geocode-result-${i}`}
            >
              <div className="text-sm font-medium text-primary">{r.label}</div>
              <div className="text-xs text-muted-foreground">
                {r.source === "destination" ? "★ Destination ITERCATUS" : "OSM"} · {r.lat.toFixed(3)}, {r.lng.toFixed(3)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
